/*
whenever updating this sdk, check if vue-frontend/src/pages/cmp/usercentricsBrowserSdkPolyfill.js
is still appropriate to make the Banner work in IE11 or use finally babel/polyfill instead
*/
import Usercentrics, {
  BaseCategory,
  UI_LAYER,
  UserDecision
} from "@usercentrics/cmp-browser-sdk";
import {
  CmpCategorySetting,
  CmpConsentDecisionToDomain,
  CmpTexts,
  ExpandedCmpService
} from "@/vue/services/cmp/CmpModel";
import {
  CmpConsentBackendService,
  CmpConsentBackendServiceImpl,
  ConsentDecisions,
  ConsentInput
} from "@/vue/services/cmp/CmpConsentBackendService";
import { categoryHasConsent } from "@/vue/services/cmp/CmpServiceUtilityClass";
import {
  blockConsentTransferCookieBaseName,
  enforceTransferOnNextOccasionThroughCookieDeletion,
  transferConsentToCurrentDomain
} from "@/vue/services/cmp/CmpConsentDomainTransferService";
import { CmpUi } from "@/vue/services/cmp/CmpUi";
import Cookies from "js-cookie";
import { BaseService } from "@usercentrics/cmp-browser-sdk/dist/src/services/SettingsV2/interfaces";
import { DefaultLabels } from "@usercentrics/cmp-browser-sdk/dist/src/services/SettingsV2/Labels/interfaces";
import {
  hideDefaultServices,
  addDefaultServicesEquivalentDecisions
} from "@/vue/services/cmp/CmpServiceDuplicatedServicesHandler";
import md5 from "md5";

const USERCENTRICS_CONSENT_STATUS_WINDOW_EVENT = "onConsentStatus";
const GOOGLE_ANALYTICS_CONSENT_TAG = "Google Analytics";
const GOOGLE_OPTIMIZE_CONSENT_TAG = "Google Optimize";

export const CONSENT_MARKED_MODIFIED_COOKIE_NAME = "consent_marked_modified";
export const GOOGLE_OPTIMIZE_ANALYTICS_CONSENT_COOKIE_NAME =
  "google_optimize_analytics_consent";

declare global {
  interface Window {
    cmpService: CmpService;
    cmpConsentBackendService: CmpConsentBackendService;
  }
}
interface VueElement extends Element {
  __vue__: {
    $children: {
      _data: {
        forceShowDenyAll: boolean;
      };
    }[];
  };
}

interface DataLayerEventWithConsentBackendFeatureToggle {
  cmpInformsConsentBackend: string;
  cmpInformsHybris: string;
}

export const CMP_CONSENT_STATUS_UPDATED_WINDOW_EVENT_NAME =
  "cmpConsentStatusUpdated";

export interface CmpService {
  categoryHasConsent(services: ExpandedCmpService[]): boolean;

  isInitialized(): boolean;

  isBannerDisplayed(): boolean;

  showBanner(closable: boolean, openPreferencesDirectly: boolean): void;

  hideBanner(): void;

  getCategorySettings(): CmpCategorySetting[];

  getConsentForService(serviceName: string): boolean;

  getDialogTextsContent(): CmpTexts | undefined;

  saveAndAcceptAll(): Promise<void>;

  saveChanges(categorySettings: CmpCategorySetting[]): Promise<void>;

  enforceNewConsentDecision(): void;

  getAllDecisions(): CmpConsentDecisionToDomain[];

  transferConsentToCurrentDomain(
    customerId: string,
    userCentricsSettings: string,
    consentArray: CmpConsentDecisionToDomain[],
    cmpCustomerConsentToken?: string,
    force?: boolean
  ): void;

  setDenyAllInFirstLayer?(denyAllInFirstLayer: boolean): void;

  restoreUserSession(controllerId: string): Promise<void>;
}

function determineWhetherConsentBackendShouldBeInformed(): boolean {
  const eventWithFeatureToggle = window
    .dataLayer[0] as DataLayerEventWithConsentBackendFeatureToggle;
  return eventWithFeatureToggle.cmpInformsConsentBackend === "true";
}

function hybrisShouldBeInformed(): boolean {
  const eventWithFeatureToggle = window
    .dataLayer[0] as DataLayerEventWithConsentBackendFeatureToggle;
  return eventWithFeatureToggle.cmpInformsHybris === "true";
}

export default function initCmpService(
  settingId: string,
  lang: string,
  hideOnStartup: boolean,
  useMarkerCookie: boolean,
  readOnly: boolean,
  customerId?: string,
  customerConsentToken?: string
) {
  window.cmpService = new CmpServiceImpl(
    settingId,
    lang,
    hideOnStartup,
    useMarkerCookie,
    readOnly,
    customerId,
    customerConsentToken
  );
  window.cmpConsentBackendService = new CmpConsentBackendServiceImpl(
    window.cmpService,
    determineWhetherConsentBackendShouldBeInformed(),
    customerId
  );
}

class CookiesAreBlockedError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, CookiesAreBlockedError.prototype);
  }
}

class ChunkLoadError extends Error {
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, ChunkLoadError.prototype);
  }
}

class CmpServiceImpl implements CmpService {
  private _usercentrics!: Usercentrics;
  private _ui?: CmpUi;
  private _usercentrics_initialized: boolean = false;
  private _banner_displayed: boolean = false;
  private readonly _customerId?: string;
  private readonly _customerConsentToken?: string;
  private readonly _requestedLang: string;
  private _settingsId: string;
  private readonly _hideOnStartup: boolean;
  private readonly _useMarkerCookie: boolean;
  private readonly _readOnly: boolean;

  constructor(
    settingsId: string,
    requestedLang: string,
    hideOnStartup: boolean,
    useMarkerCookie: boolean,
    readOnly: boolean,
    customerId?: string,
    customerConsentToken?: string
  ) {
    this._customerId = customerId;
    this._customerConsentToken = customerConsentToken;
    this._requestedLang = requestedLang;
    this._settingsId = settingsId;
    this._hideOnStartup = hideOnStartup;
    this._useMarkerCookie = useMarkerCookie;
    this._readOnly = readOnly;
    let forcePersistOnInit = false;

    const isWebView = this.isWebView();
    const determinedControllerIdByLocalStorage = JSON.parse(
      localStorage.getItem("uc_settings") || '""'
    ).controllerId;
    if (
      determinedControllerIdByLocalStorage &&
      this._customerId &&
      !this.consentModifiedMarkerCookieExists() &&
      !isWebView
    ) {
      this._usercentrics = new Usercentrics(settingsId, {
        controllerId: determinedControllerIdByLocalStorage,
        language: requestedLang
      });
    } else {
      this._usercentrics = new Usercentrics(settingsId, {
        language: requestedLang
      });
      if (
        this.consentModifiedMarkerCookieExists() ||
        (determinedControllerIdByLocalStorage === undefined && this._customerId)
      ) {
        forcePersistOnInit = true;
      }
    }

    this._usercentrics
      .init()
      .then(initialUIValue => {
        this._usercentrics_initialized = true;
        this.showCookiePolicyPreferencesLink();
        this.wireCmpConsentStatusEvents();
        this.dispatchCmpConsentStatusEvent();
        if (!hideOnStartup) {
          this.showInitialBanner(requestedLang, initialUIValue.initialLayer);
        }
        this.removeConsentModifiedCookieIfMarkerCookiesAreUsed();
        this.persistConsentToDomainIfPossible(
          forcePersistOnInit,
          this._hideOnStartup
        );
      })
      .catch(error => {
        if (
          error instanceof CookiesAreBlockedError ||
          error instanceof ChunkLoadError
        ) {
          throw error;
        }
        if (error != null) {
          console.error(
            `UserCentrics init failed. Error: ${JSON.stringify(
              error,
              Object.getOwnPropertyNames(error)
            )}`
          );
        } else {
          console.error(`UserCentrics init failed. Error was null.`);
        }
        this._usercentrics_initialized = false;
      });
  }

  private isWebView() {
    return typeof window.ucMobileSdk !== "undefined";
  }

  private consentModifiedMarkerCookieExists() {
    return (
      this._useMarkerCookie &&
      Cookies.get(CONSENT_MARKED_MODIFIED_COOKIE_NAME) === "true"
    );
  }

  private showInitialBanner(requestedLang: string, uiLayer: UI_LAYER) {
    const languageSettings = this._usercentrics.getSettingsCore().language;
    if (
      this.isLanguageAlreadySelected(
        requestedLang,
        languageSettings.selected
      ) ||
      !this.isRequestedLanguageAvailable(
        requestedLang,
        languageSettings.available
      )
    ) {
      this.showBannerDuringInit(uiLayer);
    } else {
      this._usercentrics
        .changeLanguage(requestedLang)
        .catch(ignored => {
          // no further measure is taken here
        })
        .finally(() => {
          // try to show the banner, even if changing the language did not work
          this.showBannerDuringInit(uiLayer);
        });
    }
  }

  enforceNewConsentDecision(): void {
    const cmpService = this;
    this._usercentrics.clearStorage().then(() => {
      cmpService._usercentrics = new Usercentrics(cmpService._settingsId, {
        language: cmpService._requestedLang
      });
      cmpService._usercentrics
        .init()
        .then(initialUiValue => {
          if (!cmpService._hideOnStartup) {
            cmpService.showInitialBanner(
              cmpService._requestedLang,
              initialUiValue.initialLayer
            );
          }
        })
        .catch(ignored => {
          cmpService._usercentrics_initialized = false;
        });
    });
  }

  isInitialized(): boolean {
    return this._usercentrics_initialized;
  }

  isBannerDisplayed(): boolean {
    return this._banner_displayed;
  }

  showBanner(closable: boolean, openPreferencesDirectly: boolean): void {
    if (this._usercentrics_initialized) {
      if (!this._ui) {
        console.debug("showBanner: Load UI");
        import(
          /* webpackChunkName: "cmpUi" */
          "@/vue/services/cmp/CmpUi"
        )
          .then(cmpUiModule => {
            this._ui = cmpUiModule.loadUi(
              closable,
              openPreferencesDirectly,
              this._readOnly
            );
            console.debug("showBanner: Load UI successfully finished");
            this._banner_displayed = true;
          })
          .catch(e => {
            const timestamp = new Date().getTime().toString();
            this.createCookieBlockerDetector(timestamp);
            if (this.areCookiesBlocked(timestamp)) {
              throw new CookiesAreBlockedError(
                "cookies are blocked by extension"
              );
            } else {
              throw new ChunkLoadError("Import or load CmpUi failed: " + e);
            }
          });
      } else {
        this._ui.showBanner(closable, openPreferencesDirectly);
      }
    } else {
      console.warn("Banner not being shown. Usercentrics is not initalized");
    }
  }

  private createCookieBlockerDetector(timestamp: string) {
    const cookieBlockerTrap = document.createElement("div");
    cookieBlockerTrap.id = timestamp;
    cookieBlockerTrap.className = "_cookies-cookies-bar";
    cookieBlockerTrap.style.cssText =
      "position: fixed;top: -10px;left: -10px;width: 1px;height: 1px;";
    document.body.appendChild(cookieBlockerTrap);
  }

  private areCookiesBlocked(timestamp: string): boolean {
    return (
      document!.getElementById(timestamp)!.clientHeight === 0 &&
      document!.getElementById(timestamp)!.clientWidth === 0
    );
  }

  hideBanner(): void {
    if (this._ui) {
      this._ui.hideBanner();
    }
  }

  getDialogTextsContent(): CmpTexts | undefined {
    if (!this._usercentrics_initialized) {
      return;
    }

    const saveButtonTextFallback = "Save";
    const lessButtonTextFallback = "Less";
    const moreButtonTextFallback = "More";
    const acceptButtonTextFallback = "Accept";

    const ui = this._usercentrics.getSettingsLabels() as DefaultLabels;
    const textCategory = this._usercentrics
      .getCategoriesBaseInfo()
      .find(this.isCustomTextCategory);

    let saveButtonText = saveButtonTextFallback;
    let lessButtonText = lessButtonTextFallback;
    let moreButtonText = moreButtonTextFallback;
    let acceptButtonText = acceptButtonTextFallback;

    if (textCategory != null) {
      saveButtonText = this.getTextFromCustomTextsPseudoCategory(
        textCategory,
        "save button",
        saveButtonTextFallback
      );
      lessButtonText = this.getTextFromCustomTextsPseudoCategory(
        textCategory,
        "less button",
        lessButtonTextFallback
      );
      moreButtonText = this.getTextFromCustomTextsPseudoCategory(
        textCategory,
        "more button",
        moreButtonTextFallback
      );
      acceptButtonText = this.getTextFromCustomTextsPseudoCategory(
        textCategory,
        "accept button",
        acceptButtonTextFallback
      );
    }

    const imprintLink =
      ui.links.imprint.url && ui.links.imprint.label
        ? `<a href="${ui.links.imprint.url}">${ui.links.imprint.label}</a>`
        : "";
    const policyLink =
      ui.links.privacyPolicy.url && ui.links.privacyPolicy.label
        ? `<a href="${ui.links.privacyPolicy.url}"><strong>${ui.links.privacyPolicy.label}</strong></a>`
        : "";

    const denyTranslationsMap = new Map([
      ["en", "Deny"],
      ["nl", "Weigeren"],
      ["fr", "Refuser"],
      ["de", "Ablehnen"],
      ["pl", "Odrzucić"],
      ["sv", "Avvisa"],
      ["it", "Nega"]
    ]);

    const settingsLabels = this._usercentrics.getSettingsLabels() as DefaultLabels;
    return {
      headline: ui.firstLayer.title,
      descriptionBanner: this.replacePlainTextWithLinks(ui),
      descriptionPreferences: ui.secondLayer.description,
      accept: acceptButtonText,
      acceptAll: ui.buttons.acceptAll,
      deny: denyTranslationsMap.get(this._requestedLang) || "Deny",
      denyAll: settingsLabels.secondLayer.denyButtonLabel,
      save: saveButtonText,
      learnMore: moreButtonText,
      showLess: lessButtonText,
      policyLink: policyLink,
      imprintLink: imprintLink
    };
  }

  private replacePlainTextWithLinks(ui: DefaultLabels): string {
    let descriptionBanner = ui.firstLayer.description.default;

    descriptionBanner = this.replaceText(
      descriptionBanner,
      ui.buttons.denyAll,
      this.buildClickHereLink(ui)
    );

    if (ui.links.privacyPolicy.label) {
      descriptionBanner = this.replaceText(
        descriptionBanner,
        ui.links.privacyPolicy.label,
        this.buildPrivacyPolicyLink(ui)
      );
    }

    return descriptionBanner;
  }

  private buildPrivacyPolicyLink(ui: DefaultLabels) {
    return ui.links.privacyPolicy.url && ui.links.privacyPolicy.label
      ? `<a href="${ui.links.privacyPolicy.url}"><strong>${ui.links.privacyPolicy.label}</strong></a>`
      : "";
  }

  private buildClickHereLink(ui: DefaultLabels) {
    const defaultUi = ui;
    const clickHereLabel = defaultUi.buttons.denyAll;
    return `<a id="deny-consents-close-banner" style="cursor: pointer"><strong>${clickHereLabel}</strong></a>`;
  }

  private replaceText(
    originalText: string,
    textToReplace: string,
    replacingLink: string
  ) {
    if (originalText.includes(textToReplace)) {
      originalText = originalText.replace(textToReplace, replacingLink);
    } else {
      console.debug(
        "'" + textToReplace + "' text not found. Could not build the link."
      );
    }
    return originalText;
  }

  private getTextFromCustomTextsPseudoCategory(
    textCategory: BaseCategory,
    serviceName: string,
    fallback: string
  ): string {
    const service = textCategory.services.find(s => s.name === serviceName);
    if (service != null && service.description != null) {
      return service.description.toString();
    }
    return fallback;
  }

  getCategorySettings(): CmpCategorySetting[] {
    if (!this._usercentrics_initialized) {
      return [];
    }

    let categories = this._usercentrics
      .getCategoriesBaseInfo()
      .filter(category => !this.isCustomTextCategory(category));

    hideDefaultServices(categories);

    categories.forEach(category => {
      category.services = this.setServicesOldConsent(category.services);
    });
    categories = this.setCategoriesHasConsent(
      categories as CmpCategorySetting[]
    );
    return categories as CmpCategorySetting[];
  }

  categoryHasConsent = (services: ExpandedCmpService[]) =>
    categoryHasConsent(services);

  private isCustomTextCategory(category: BaseCategory) {
    return category.label == "CustomTexts";
  }

  getConsentForService(serviceName: string): boolean {
    const services = this._usercentrics
      .getCategoriesBaseInfo()
      .flatMap(c => c.services);
    const service = services.find(e => e.name === serviceName);
    if (service) {
      return service.consent.status;
    }
    return false;
  }

  saveChanges(categorySettings: CmpCategorySetting[]): Promise<void> {
    if (!this._usercentrics_initialized || this._readOnly) {
      return Promise.reject();
    }
    let decisions = this.getUserDecisionsForChangedServices(categorySettings);
    decisions = addDefaultServicesEquivalentDecisions(decisions);
    if (this.areAllNonEssentialCategoriesRejected(categorySettings)) {
      // in order to prevent the banner to show up on the next page
      return this._usercentrics
        .denyAllServices()
        .then(() =>
          this.persistConsentToDomainIfPossible(true, this._hideOnStartup)
        );
    } else if (decisions.length > 0) {
      return this._usercentrics
        .updateServices(decisions)
        .then(() =>
          this.persistConsentToDomainIfPossible(true, this._hideOnStartup)
        );
    }
    return Promise.resolve();
  }

  saveAndAcceptAll(): Promise<void> {
    if (!this._usercentrics_initialized || this._readOnly) {
      return Promise.reject();
    }
    return this._usercentrics
      .acceptAllServices()
      .then(() =>
        this.persistConsentToDomainIfPossible(true, this._hideOnStartup)
      );
  }

  private dispatchCmpConsentStatusEvent() {
    const statusEvent = new CustomEvent(
      CMP_CONSENT_STATUS_UPDATED_WINDOW_EVENT_NAME
    );
    window.dispatchEvent(statusEvent);
  }

  private wireCmpConsentStatusEvents() {
    window.addEventListener(USERCENTRICS_CONSENT_STATUS_WINDOW_EVENT, () => {
      if (this._usercentrics_initialized) {
        this.dispatchCmpConsentStatusEvent();
      }
    });
  }

  private setServicesOldConsent(services: BaseService[]): ExpandedCmpService[] {
    return services.map(service => {
      return Object.assign(
        {
          oldConsent: service.consent.status
        },
        service
      );
    }) as ExpandedCmpService[];
  }
  private setCategoriesHasConsent(
    categories: CmpCategorySetting[]
  ): CmpCategorySetting[] {
    return categories.map(category => {
      return Object.assign(
        {
          hasConsent:
            category.isEssential || this.categoryHasConsent(category.services)
        },
        category
      );
    });
  }

  private showCookiePolicyPreferencesLink(): void {
    if (document.readyState !== "loading") {
      this.activateManageConsentButton();
    } else {
      document.addEventListener(
        "DOMContentLoaded",
        this.activateManageConsentButton
      );
    }
  }

  private activateManageConsentButton() {
    const cmpManageConsentButton = document.querySelector(
      ".cmp-manageconsentbutton"
    );
    if (cmpManageConsentButton) {
      cmpManageConsentButton.setAttribute(
        "style",
        "display: inline !important"
      );
      cmpManageConsentButton.addEventListener("click", () => {
        window.cmpService.showBanner(true, true);
      });
    }
  }

  private isLanguageAlreadySelected(
    requestedLang: string,
    storedLang: string
  ): boolean {
    return storedLang === requestedLang;
  }

  private isRequestedLanguageAvailable(
    lang: string,
    available: string[]
  ): boolean {
    return available.includes(lang);
  }

  private showBannerDuringInit(uiLayer: UI_LAYER) {
    if (uiLayer === UI_LAYER.FIRST_LAYER) {
      this.showBanner(false, false);
    }
  }

  getAllDecisions(): CmpConsentDecisionToDomain[] {
    const categoriesSettings = this.getCategorySettings();
    if (categoriesSettings) {
      const usedCategories = categoriesSettings.filter(
        category => !this.isCustomTextCategory(category)
      );
      return usedCategories.flatMap(category =>
        category.services.map(service => {
          return {
            service: service.id + " " + service.name,
            consent: service.consent.status
          };
        })
      );
    }
    return [];
  }

  getAllDecisionsForConsentBackend(): ConsentDecisions[] {
    const categoriesSettings = this.getCategorySettings();
    if (categoriesSettings) {
      const usedCategories = categoriesSettings.filter(
        category => !this.isCustomTextCategory(category)
      );
      return usedCategories.flatMap(category =>
        category.services.map(service => {
          return {
            code: service.id,
            name: service.name,
            selected: service.consent.status
          };
        })
      );
    }
    return [];
  }

  private areAllNonEssentialCategoriesRejected(
    categorySettings: CmpCategorySetting[]
  ) {
    return categorySettings
      .filter(category => !category.isEssential)
      .flatMap(category => category.services)
      .every(service => !service.consent.status);
  }

  private getUserDecisionsForChangedServices(
    categorySettings: CmpCategorySetting[]
  ): UserDecision[] {
    return categorySettings
      .flatMap(category => category.services)
      .filter(service => service.consent.status !== service.oldConsent)
      .map(service => {
        return { serviceId: service.id, status: service.consent.status };
      });
  }

  private persistConsentToDomainIfPossible(
    consentModified: boolean = true,
    cmpHideOnStartup: boolean
  ) {
    if (this._readOnly) {
      return;
    }
    this.setGoogleConsentCookie();
    const ucControllerId = this._usercentrics.getControllerId();

    if (!cmpHideOnStartup) {
      if (this._customerId) {
        if (hybrisShouldBeInformed()) {
          const cmpConsents = this.getAllDecisions();
          transferConsentToCurrentDomain(
            this._customerId,
            ucControllerId,
            cmpConsents,
            this._customerConsentToken,
            consentModified
          );
        }

        this.persistConsentDecisionInConsentBackend(consentModified);
      } else {
        if (consentModified) {
          this.setConsentModifiedCookieIfMarkerCookiesAreUsed();
          this.enforceTransferOnNextOccasionThroughCookieDeletionIfMarkerCookiesAreUsed(
            ucControllerId
          );
        }
      }
    }
  }

  private persistConsentDecisionInConsentBackend(consentModified: boolean) {
    const preventMultipleTransfersCookieName =
      blockConsentTransferCookieBaseName + md5(this._customerId);
    const controllerId = this._usercentrics.getControllerId();
    if (
      consentModified ||
      Cookies.get(preventMultipleTransfersCookieName) !== controllerId
    ) {
      const consentDecision: ConsentInput = {
        accountId: this._customerId,
        uCControllerId: {
          controllerId: controllerId,
          uCConfigurationId: this._settingsId
        },
        consents: this.getAllDecisionsForConsentBackend()
      };

      window.cmpConsentBackendService
        .storeConsentInConsentBackendServiceForLoggedInUsers(consentDecision)
        .then(response => {
          if (!hybrisShouldBeInformed()) {
            if (response !== undefined) {
              Cookies.set(preventMultipleTransfersCookieName, controllerId, {
                secure: true,
                sameSite: "strict",
                expires: 2 //two days from now REST call will be repeated for fault tolerance
              });
            }
          }
        })
        .catch(e => {
          console.error(e);
        });
    }
  }

  private enforceTransferOnNextOccasionThroughCookieDeletionIfMarkerCookiesAreUsed(
    ucControllerId: string
  ) {
    if (this._useMarkerCookie) {
      enforceTransferOnNextOccasionThroughCookieDeletion(ucControllerId);
    }
  }

  private setConsentModifiedCookieIfMarkerCookiesAreUsed() {
    if (this._useMarkerCookie) {
      Cookies.set(CONSENT_MARKED_MODIFIED_COOKIE_NAME, "true", {
        secure: true,
        sameSite: "strict"
      });
    }
  }

  private removeConsentModifiedCookieIfMarkerCookiesAreUsed() {
    if (this._useMarkerCookie) {
      if (this._customerId) {
        Cookies.remove(CONSENT_MARKED_MODIFIED_COOKIE_NAME);
      }
    }
  }

  private setGoogleConsentCookie() {
    const hasConsent = this.hasGoogleOptimizeAndAnalyticsConsent();
    if (hasConsent) {
      Cookies.set(
        GOOGLE_OPTIMIZE_ANALYTICS_CONSENT_COOKIE_NAME,
        hasConsent.toString()
      );
    }
  }

  private hasGoogleOptimizeAndAnalyticsConsent() {
    return (
      this.getConsentForService(GOOGLE_ANALYTICS_CONSENT_TAG) &&
      this.getConsentForService(GOOGLE_OPTIMIZE_CONSENT_TAG)
    );
  }

  transferConsentToCurrentDomain(
    customerId: string,
    userCentricsSettings: string,
    consentArray: CmpConsentDecisionToDomain[],
    cmpCustomerConsentToken?: string,
    force?: boolean
  ): void {
    transferConsentToCurrentDomain(
      customerId,
      userCentricsSettings,
      consentArray,
      cmpCustomerConsentToken,
      force
    );
  }

  setDenyAllInFirstLayer(denyAllInFirstLayer: boolean): void {
    const x: VueElement[] = Array.from(document.querySelectorAll("*"));
    x!.find((e: VueElement) => {
      return e!.__vue__;
    })!.__vue__.$children[0]._data.forceShowDenyAll = denyAllInFirstLayer;
  }

  async restoreUserSession(controllerId: string): Promise<void> {
    await this._usercentrics.restoreUserSession(controllerId);
  }
}
